import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import * as apiSettings from 'api/apiSettings';
import type { SettingsParamsType } from '@econtractor/types';
import { normalizeSettingsParams } from './defaults';

const useMainSettings = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const { showToast } = useToast();
    const { settings, setSettings, localSettings, dispatchLocalSettings } = useSettings();
    const [isSaving, setIsSaving] = useState(false);

    const params = useMemo(
        () => normalizeSettingsParams(localSettings?.global?.params),
        [localSettings?.global?.params],
    );

    const savedParams = useMemo(
        () => normalizeSettingsParams(settings?.global?.params),
        [settings?.global?.params],
    );

    const isDirty = useMemo(() => {
        const localRaw = localSettings?.global?.params ?? null;
        const savedRaw = settings?.global?.params ?? null;
        // No params yet anywhere — nothing to save
        if (localRaw == null && savedRaw == null) return false;
        // First edit creates local structure before DB has it
        if (localRaw == null || savedRaw == null) return true;
        return JSON.stringify(params) !== JSON.stringify(savedParams);
    }, [localSettings?.global?.params, settings?.global?.params, params, savedParams]);

    const setParam = <K extends keyof SettingsParamsType>(key: K, value: SettingsParamsType[K]) => {
        const next: SettingsParamsType = { ...params, [key]: value };

        if (key === 'objectPaymentControl' && !value) {
            next.autoSyncPayStatus = false;
            next.manualPayStatus = false;
        }
        if (key === 'tasksModule' && !value) {
            next.tasksAutoAcceptOwn = false;
            next.tasksForLeads = false;
            next.tasksForObjects = false;
        }

        dispatchLocalSettings({
            type: 'SET_FIELD',
            field: 'global.params',
            value: next,
        });
    };

    const handleSave = async () => {
        if (isSaving || !isDirty || !localSettings) return;
        setIsSaving(true);
        try {
            const nextSettings = {
                ...localSettings,
                global: {
                    ...(localSettings.global || {}),
                    params,
                },
            };
            const ok = await apiSettings.setSettings(nextSettings);
            if (ok) {
                dispatchLocalSettings({ type: 'SET', settings: nextSettings });
                setSettings(nextSettings);
                showToast(t('messSaved'), 'success');
            } else {
                showToast(t('common:messError'), 'danger');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return {
        params,
        isDirty,
        isSaving,
        setParam,
        handleSave,
    };
};

export default useMainSettings;
